import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { statsAPI } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Calculator, Calendar, FileText, TrendingUp } from "lucide-react";
import { DateField } from "@/components/ui/date-field";

const formatEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

const PERIOD_PRESETS = [
  { id: "this_month", label: "Ce mois" },
  { id: "last_month", label: "Mois dernier" },
  { id: "this_quarter", label: "Ce trimestre" },
  { id: "last_quarter", label: "Trim. dernier" },
  { id: "this_year", label: "Cette année" },
  { id: "last_year", label: "Année dernière" },
  { id: "custom", label: "Personnalise" },
];

function getPresetDates(id) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3);
  switch (id) {
    case "this_month":
      return { start: `${y}-${String(m + 1).padStart(2, "0")}-01`, end: `${y}-${String(m + 1).padStart(2, "0")}-31`, group_by: "month" };
    case "last_month": {
      const d = new Date(y, m - 1, 1);
      const ey = d.getFullYear(), em = d.getMonth();
      return { start: `${ey}-${String(em + 1).padStart(2, "0")}-01`, end: `${ey}-${String(em + 1).padStart(2, "0")}-31`, group_by: "month" };
    }
    case "this_quarter":
      return { start: `${y}-${String(q * 3 + 1).padStart(2, "0")}-01`, end: `${y}-${String(q * 3 + 3).padStart(2, "0")}-31`, group_by: "month" };
    case "last_quarter": {
      const lq = q === 0 ? 3 : q - 1;
      const ly = q === 0 ? y - 1 : y;
      return { start: `${ly}-${String(lq * 3 + 1).padStart(2, "0")}-01`, end: `${ly}-${String(lq * 3 + 3).padStart(2, "0")}-31`, group_by: "month" };
    }
    case "this_year":
      return { start: `${y}-01-01`, end: `${y}-12-31`, group_by: "month" };
    case "last_year":
      return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31`, group_by: "month" };
    default:
      return { start: `${y}-01-01`, end: `${y}-12-31`, group_by: "month" };
  }
}

function formatPeriod(key) {
  if (!key) return "";
  if (key.includes("-T")) return key.replace("-T", " T");
  if (key.length === 7) {
    const [y, m] = key.split("-");
    const months = ["Jan", "Fev", "Mar", "Avr", "Mai", "Jun", "Jul", "Aou", "Sep", "Oct", "Nov", "Dec"];
    return `${months[parseInt(m) - 1]} ${y}`;
  }
  return key;
}

export default function TvaPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("this_year");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [groupBy, setGroupBy] = useState("month");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let params;
      if (preset === "custom") {
        params = { start: customStart, end: customEnd, group_by: groupBy };
      } else {
        params = getPresetDates(preset);
      }
      const r = await statsAPI.tva(params);
      setData(r.data);
    } catch (e) { toast.error("Erreur chargement TVA"); }
    setLoading(false);
  }, [preset, customStart, customEnd, groupBy]);

  useEffect(() => { load(); }, [load]);

  const collectee = data?.tva_collectee || {};
  const deductible = data?.tva_deductible || {};
  const nette = data?.tva_nette || 0;
  const allRates = useMemo(() => data?.all_rates || [], [data]);
  const periods = collectee.by_period || [];
  const activeRates = useMemo(() => allRates.filter(r => (collectee.by_rate || {})[r]), [allRates, collectee.by_rate]);
  const regimeTva = data?.regime_tva || "encaissements";

  // Chart data
  const chartData = periods.map(p => {
    const row = { period: formatPeriod(p.period), tva_collectee: p.total_tva };
    return row;
  });

  const RATE_COLORS = { "20": "#4F46E5", "19.6": "#7C3AED", "10": "#0891B2", "5.5": "#059669", "0": "#94A3B8", "0.0": "#94A3B8" };

  return (
    <div className="page-container" data-testid="tva-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>TVA</h1>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-slate-500">Recapitulatif TVA collectee et deductible</p>
          {data && (
            <Badge className={`text-[10px] px-2 py-0.5 ${regimeTva === "encaissements" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`} data-testid="regime-tva-badge">
              {regimeTva === "encaissements" ? "Sur les encaissements" : "Sur les debits"}
            </Badge>
          )}
        </div>
      </div>

      {/* Period selector */}
      <Card className="p-4 border border-slate-200 rounded-lg bg-white mb-6" data-testid="tva-period-selector">
        <div className="flex flex-wrap items-center gap-2">
          <Calendar size={14} className="text-slate-400" />
          {PERIOD_PRESETS.map(p => (
            <Button key={p.id} size="sm" variant={preset === p.id ? "default" : "outline"}
              className={`h-7 text-[11px] ${preset === p.id ? "bg-slate-900 text-white" : ""}`}
              onClick={() => setPreset(p.id)} data-testid={`tva-period-${p.id}`}>
              {p.label}
            </Button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-3 mt-3">
            <DateField value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-300 rounded-md" data-testid="tva-custom-start" />
            <span className="text-xs text-slate-400">au</span>
            <DateField value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-300 rounded-md" data-testid="tva-custom-end" />
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-300 rounded-md bg-white" data-testid="tva-group-by">
              <option value="month">Par mois</option>
              <option value="quarter">Par trimestre</option>
              <option value="year">Par année</option>
            </select>
            <Button size="sm" className="h-8 text-xs" onClick={load} data-testid="tva-apply">Appliquer</Button>
          </div>
        )}
      </Card>

      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="tva-summary-cards">
            <Card className="p-5 border border-slate-200 rounded-lg bg-white">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <ArrowUpRight size={16} className="text-red-600" />
                </div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">TVA Collectee</span>
              </div>
              <p className="text-2xl font-bold text-red-700" data-testid="tva-collectee-total">{formatEur(collectee.total)}</p>
              <p className="text-[10px] text-slate-400 mt-1">sur {formatEur(collectee.total_ht)} HT | {data?.doc_count || 0} {regimeTva === "encaissements" ? "factures avec règlements" : "factures"}</p>
            </Card>
            <Card className="p-5 border border-slate-200 rounded-lg bg-white">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <ArrowDownRight size={16} className="text-emerald-600" />
                </div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">TVA Deductible</span>
              </div>
              <p className="text-2xl font-bold text-emerald-700" data-testid="tva-deductible-total">{formatEur(deductible.total)}</p>
              <p className="text-[10px] text-slate-400 mt-1">sur {formatEur(deductible.total_ht)} HT | {data?.supplier_count || 0} doc. fournisseurs</p>
            </Card>
            <Card className={`p-5 border rounded-lg ${nette >= 0 ? "border-red-200 bg-red-50/30" : "border-emerald-200 bg-emerald-50/30"}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${nette >= 0 ? "bg-red-100" : "bg-emerald-100"}`}>
                  <Calculator size={16} className={nette >= 0 ? "text-red-600" : "text-emerald-600"} />
                </div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">{nette >= 0 ? "TVA a payer" : "Credit de TVA"}</span>
              </div>
              <p className={`text-2xl font-bold ${nette >= 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="tva-nette-total">{formatEur(Math.abs(nette))}</p>
              <p className="text-[10px] text-slate-400 mt-1">Collectee - Deductible</p>
            </Card>
          </div>

          {/* Chart */}
          {chartData.length > 1 && (
            <Card className="p-6 border border-slate-200 rounded-lg bg-white mb-6" data-testid="tva-chart">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp size={14} className="text-indigo-600" /> Evolution TVA collectee par période
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={v => `${v} \u20ac`} />
                  <Tooltip formatter={(v) => [formatEur(v), "TVA collectee"]} contentStyle={{ borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "12px" }} />
                  <Bar dataKey="tva_collectee" fill="#4F46E5" radius={[4, 4, 0, 0]} name="TVA collectee" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Breakdown by rate */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* TVA Collectée by rate */}
            <Card className="p-5 border border-slate-200 rounded-lg bg-white" data-testid="tva-collectee-rates">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <ArrowUpRight size={14} className="text-red-600" /> TVA Collectee par taux
              </h3>
              <div className="space-y-2">
                {allRates.map(rate => {
                  const amount = (collectee.by_rate || {})[rate] || 0;
                  if (amount === 0) return null;
                  const pct = collectee.total ? Math.round((amount / collectee.total) * 100) : 0;
                  return (
                    <div key={`c-${rate}`} className="flex items-center gap-3" data-testid={`tva-rate-${rate}`}>
                      <Badge className="text-[10px] px-2 py-0.5 font-bold text-white shrink-0" style={{ backgroundColor: RATE_COLORS[rate] || "#64748B" }}>
                        {rate}%
                      </Badge>
                      <div className="flex-1">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: RATE_COLORS[rate] || "#64748B" }} />
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-slate-800 w-[100px] text-right">{formatEur(amount)}</span>
                    </div>
                  );
                })}
                {Object.keys(collectee.by_rate || {}).length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">Aucune donnee pour cette période</p>
                )}
              </div>
            </Card>

            {/* TVA Déductible by rate */}
            <Card className="p-5 border border-slate-200 rounded-lg bg-white" data-testid="tva-deductible-rates">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <ArrowDownRight size={14} className="text-emerald-600" /> TVA Deductible par taux
              </h3>
              <div className="space-y-2">
                {allRates.map(rate => {
                  const amount = (deductible.by_rate || {})[rate] || 0;
                  if (amount === 0) return null;
                  return (
                    <div key={`d-${rate}`} className="flex items-center gap-3">
                      <Badge className="text-[10px] px-2 py-0.5 font-bold text-white shrink-0" style={{ backgroundColor: RATE_COLORS[rate] || "#64748B" }}>
                        {rate}%
                      </Badge>
                      <div className="flex-1">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: deductible.total ? `${Math.round((amount / deductible.total) * 100)}%` : "0%" }} />
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-slate-800 w-[100px] text-right">{formatEur(amount)}</span>
                    </div>
                  );
                })}
                {Object.keys(deductible.by_rate || {}).length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">Aucune donnee fournisseur pour cette période</p>
                )}
              </div>
            </Card>
          </div>

          {/* Detailed period table */}
          {periods.length > 0 && (
            <Card className="p-5 border border-slate-200 rounded-lg bg-white" data-testid="tva-period-table">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileText size={14} className="text-indigo-600" /> Detail par période
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs bg-white">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Période</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-slate-600">CA HT</th>
                      {activeRates.map(rate => (
                        <th key={rate} className="text-right py-2.5 px-3 font-semibold text-slate-600">TVA {rate}%</th>
                      ))}
                      <th className="text-right py-2.5 px-3 font-semibold text-slate-800">Total TVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map(p => (
                      <tr key={p.period} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-2.5 px-3 font-medium text-slate-800">{formatPeriod(p.period)}</td>
                        <td className="py-2.5 px-3 text-right text-slate-600">{formatEur(p.total_ht)}</td>
                        {activeRates.map(rate => (
                          <td key={rate} className="py-2.5 px-3 text-right text-slate-600">{formatEur((p.by_rate || {})[rate] || 0)}</td>
                        ))}
                        <td className="py-2.5 px-3 text-right font-bold text-red-700">{formatEur(p.total_tva)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                      <td className="py-2.5 px-3 text-slate-800">TOTAL</td>
                      <td className="py-2.5 px-3 text-right text-slate-800">{formatEur(collectee.total_ht)}</td>
                      {activeRates.map(rate => (
                        <td key={rate} className="py-2.5 px-3 text-right" style={{ color: RATE_COLORS[rate] || "#334155" }}>
                          {formatEur((collectee.by_rate || {})[rate] || 0)}
                        </td>
                      ))}
                      <td className="py-2.5 px-3 text-right text-red-700">{formatEur(collectee.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
